import { clsx, type ClassValue } from "clsx";
import { format } from "date-fns";
import { twMerge } from "tailwind-merge";
import { formatCurrency } from "./currency";
import { type Subscription, type PaymentHistory } from "@/types/subscription";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the last payment from a subscription's payment history
 */
export function getLastPayment(
  subscription: Subscription,
): PaymentHistory | null {
  if (
    !subscription.paymentHistory ||
    subscription.paymentHistory.length === 0
  ) {
    return null;
  }

  // Sort by date descending and get the most recent
  const sortedPayments = [...subscription.paymentHistory].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return sortedPayments[0];
}

/**
 * Format last payment information for display
 */
export function formatLastPayment(lastPayment: PaymentHistory | null): {
  text: string;
  shortText: string;
  amount: string;
} {
  if (!lastPayment) {
    return {
      text: "No payment history",
      shortText: "No payments",
      amount: "-",
    };
  }

  const paymentDate = new Date(lastPayment.date);
  const isRecent =
    Date.now() - paymentDate.getTime() < 30 * 24 * 60 * 60 * 1000; // 30 days

  return {
    text: `Last paid ${format(paymentDate, "MMM dd, yyyy")}`,
    shortText: isRecent
      ? format(paymentDate, "MMM dd")
      : format(paymentDate, "MMM yyyy"),
    amount: formatCurrency(lastPayment.amount, lastPayment.currency),
  };
}

/**
 * Get payment context for Mark as Paid actions
 */
export function getPaymentContext(subscription: Subscription) {
  const lastPayment = getLastPayment(subscription);
  const paymentCount = subscription.paymentHistory?.length || 0;

  return {
    lastPayment,
    paymentCount,
    hasHistory: paymentCount > 0,
    suggestedAmount: lastPayment ? lastPayment.amount : subscription.amount,
    totalPaid:
      subscription.paymentHistory?.reduce(
        (sum, payment) =>
          payment.status === "paid" ? sum + payment.amount : sum,
        0,
      ) || 0,
  };
}
